/**
 * Распознаёт ссылку на место Писания в строке поиска (без ИИ):
 * URL приложения, t.me startapp, или текст вида «Евреям 5:11», «Евр 5:11».
 * Возвращает { translation?, bookId, chapter, verse? } или null.
 */

import ruBookShortToId from '../data/ruBookShortToId.json';

function normBookKey(s) {
  if (!s || typeof s !== 'string') return '';
  return s
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^\p{L}0-9\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseNumericPath(pathname) {
  const p = String(pathname || '').replace(/\/+/g, '/');
  const m = p.match(/\/([a-z0-9_-]+)\/(\d+)\/(\d+)(?:\/(\d+))?$/i);
  if (!m) return null;
  return {
    translation: m[1],
    bookId: parseInt(m[2], 10),
    chapter: parseInt(m[3], 10),
    verse: m[4] != null ? parseInt(m[4], 10) : null,
  };
}

function parseStartApp(q) {
  const m = String(q).match(/startapp=([A-Za-z0-9_]+)/i);
  if (!m) return null;
  const parts = m[1].split('_').filter(Boolean);
  if (parts.length < 3) return null;
  const isOld = /^\d+$/.test(parts[0]);
  if (isOld) {
    return {
      bookId: parseInt(parts[0], 10),
      chapter: parseInt(parts[1], 10),
      verse: parseInt(parts[2], 10),
    };
  }
  return {
    translation: parts[0],
    bookId: parseInt(parts[1], 10),
    chapter: parseInt(parts[2], 10),
    verse: parseInt(parts[3], 10),
  };
}

function resolveBookId(bookPartRaw, books) {
  const bookKey = normBookKey(bookPartRaw);
  if (!bookKey || bookKey.length < 2 || !Array.isArray(books) || books.length === 0) return null;

  let bestId = null;
  let bestScore = 0;

  for (const book of books) {
    const id = Number(book.id);
    if (!Number.isFinite(id)) continue;
    const nn = normBookKey(book.name || '');
    const shortNorm = normBookKey(book.nameShort || '');

    if (shortNorm && shortNorm === bookKey) return id;
    if (nn && nn === bookKey) return id;

    let score = 0;

    if (shortNorm) {
      if (shortNorm.startsWith(bookKey) && bookKey.length >= 2) {
        score = Math.max(score, Math.min(95, 42 + bookKey.length));
      }
      if (bookKey.startsWith(shortNorm) && shortNorm.length >= 2) {
        score = Math.max(score, Math.min(92, 38 + shortNorm.length));
      }
    }

    if (nn) {
      if (nn.startsWith(bookKey)) score = Math.max(score, Math.min(80, 40 + bookKey.length));
      else if (bookKey.startsWith(nn) && nn.length >= 4) score = Math.max(score, Math.min(60, 20 + nn.length));
    }

    const words = [];
    if (nn) words.push(...nn.split(' ').filter(Boolean));
    if (shortNorm && shortNorm.length >= 2 && !words.includes(shortNorm)) {
      words.push(shortNorm);
    }
    for (const w of words) {
      if (w === bookKey) {
        score = Math.max(score, 100);
        break;
      }
      if (w.startsWith(bookKey) && bookKey.length >= 2) {
        score = Math.max(score, Math.min(90, 30 + Math.min(w.length, bookKey.length)));
      }
      if (bookKey.length >= 3 && w.startsWith(bookKey)) {
        score = Math.max(score, 35 + bookKey.length);
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestId = id;
    }
  }

  // Не угадываем книгу по слабому совпадению (избегаем ложных переходов).
  if (bestScore < 32) {
    const ruId = ruBookShortToId[bookKey];
    if (Number.isFinite(ruId)) return ruId;
    return null;
  }
  return bestId;
}

/**
 * @param {string} raw
 * @param {Array<{ id: number|string, name: string, nameShort?: string }>} books
 * @param {string} defaultTranslation
 * @returns {{ translation: string, bookId: number, chapter: number, verse?: number } | null}
 */
export function parseVerseReferenceFromSearchQuery(raw, books, defaultTranslation = 'synodal') {
  const q = String(raw || '').trim();
  if (!q) return null;

  // Полный URL
  try {
    const u = new URL(q);
    const fromPath = parseNumericPath(u.pathname);
    if (fromPath && Number.isFinite(fromPath.bookId) && Number.isFinite(fromPath.chapter)) {
      return {
        translation: fromPath.translation || defaultTranslation,
        bookId: fromPath.bookId,
        chapter: fromPath.chapter,
        verse: fromPath.verse != null ? fromPath.verse : undefined,
      };
    }
    const fromStart = parseStartApp(`${u.search || ''}&${u.hash || ''}`);
    if (fromStart && Number.isFinite(fromStart.bookId) && Number.isFinite(fromStart.chapter)) {
      return {
        translation: fromStart.translation || defaultTranslation,
        bookId: fromStart.bookId,
        chapter: fromStart.chapter,
        verse: Number.isFinite(fromStart.verse) ? fromStart.verse : undefined,
      };
    }
  } catch {
    /* not a full URL */
  }

  // Фрагмент с startapp=
  const inlineStart = parseStartApp(q);
  if (inlineStart && Number.isFinite(inlineStart.bookId) && Number.isFinite(inlineStart.chapter)) {
    return {
      translation: inlineStart.translation || defaultTranslation,
      bookId: inlineStart.bookId,
      chapter: inlineStart.chapter,
      verse: Number.isFinite(inlineStart.verse) ? inlineStart.verse : undefined,
    };
  }

  // Путь без домена: /synodal/58/9/11
  const pathLike = q.startsWith('/') ? q : `/${q}`;
  const localPath = parseNumericPath(pathLike);
  if (localPath && Number.isFinite(localPath.bookId) && Number.isFinite(localPath.chapter)) {
    return {
      translation: localPath.translation || defaultTranslation,
      bookId: localPath.bookId,
      chapter: localPath.chapter,
      verse: localPath.verse != null ? localPath.verse : undefined,
    };
  }

  // «Книга 5:11» или «Книга 5.11» (между названием и главой есть пробел)
  const withVerse = q.match(/^\s*(.+?)\s+(\d{1,3})\s*[:,.．\uFF1A]\s*(\d{1,3})\s*$/u);
  if (withVerse) {
    const bookPart = withVerse[1].trim();
    const chapter = parseInt(withVerse[2], 10);
    const verse = parseInt(withVerse[3], 10);
    const bookId = resolveBookId(bookPart, books);
    if (bookId != null && Number.isFinite(chapter) && Number.isFinite(verse)) {
      return { translation: defaultTranslation, bookId, chapter, verse };
    }
  }

  // «Мф3:33», «Ин3:16» — сокращение слитно с номером главы
  const compactVerse = q.match(
    /^\s*([A-Za-zА-Яа-яЁёІіЇїЄєҐґ]{2,14})(\d{1,3})\s*[:,.．\uFF1A]\s*(\d{1,3})\s*$/u
  );
  if (compactVerse) {
    const bookPart = compactVerse[1].trim();
    const chapter = parseInt(compactVerse[2], 10);
    const verse = parseInt(compactVerse[3], 10);
    const bookId = resolveBookId(bookPart, books);
    if (bookId != null && Number.isFinite(chapter) && Number.isFinite(verse)) {
      return { translation: defaultTranslation, bookId, chapter, verse };
    }
  }

  // Только глава: «Иоанна 3» — осторожно, короткие названия могут мазать; требуем уверенное совпадение книги.
  const chapterOnly = q.match(/^\s*(.+?)\s+(\d{1,3})\s*$/u);
  if (chapterOnly) {
    const bookPart = chapterOnly[1].trim();
    const chapter = parseInt(chapterOnly[2], 10);
    const bookId = resolveBookId(bookPart, books);
    if (bookId != null && Number.isFinite(chapter) && normBookKey(bookPart).length >= 3) {
      return { translation: defaultTranslation, bookId, chapter };
    }
  }

  return null;
}
